import Aura from '@primeng/themes/aura';
import { definePreset } from '@primeng/themes';

export const MyPreset = definePreset(Aura, {
  components: {
    card: {
      root: {
        shadow: 'var(--shadow-card)',
      },
    },
    // PrimeNG's default editor preset maps both toolbar and content to
    // `{content.background}` (→ `var(--color-surface)`), which renders the
    // Quill editor on a different gray than Decision/Action/Discussion form
    // fields. Repoint to `--color-canvas` (the formField background) so the
    // editor matches input fields in both light and dark mode. Borders and
    // text follow the same form-field tokens.
    editor: {
      toolbar: {
        background: 'var(--color-canvas)',
        borderColor: 'var(--color-divider)',
      },
      content: {
        background: 'var(--color-canvas)',
        borderColor: 'var(--color-divider)',
        color: 'var(--color-foreground)',
      },
    },
    // Aura's dark off-state track is `{surface.700}` (#0f1e2e), which is the
    // card background (--color-surface #0e1a2e) to the eye: an unchecked
    // switch rendered as a lone grey dot. Lift the track and handle two steps
    // so it reads as a switch. Checked and disabled states are unchanged.
    toggleswitch: {
      colorScheme: {
        dark: {
          root: {
            background: '{surface.500}',
            hoverBackground: '{surface.400}',
          },
          handle: {
            background: '{surface.300}',
            hoverBackground: '{surface.200}',
          },
        },
      },
    },
    // Same story for tooltips: Aura's dark tooltip is `{surface.700}`, the card background, so a
    // tooltip over the dashboard had no edge and its text read as stray words on the page. Lift
    // it to `{surface.500}`; the white text stays well above contrast minimums.
    tooltip: {
      colorScheme: {
        dark: {
          root: {
            background: '{surface.500}',
          },
        },
      },
    },
  },
  primitive: {
    borderRadius: {
      none: '0',
      xs: '2px',
      sm: '4px',
      md: '6px',
      lg: '8px',
      xl: '12px',
    },
  },
  semantic: {
    transitionDuration: '0.2s',
    focusRing: {
      width: '1px',
      style: 'solid',
      color: '{primary.color}',
      offset: '2px',
      shadow: 'none',
    },
    disabledOpacity: '0.6',
    iconSize: '1rem',
    anchorGutter: '2px',
    primary: {
      50: '#f0f6fc',
      100: '#dde9f5',
      200: '#bcd2eb',
      300: '#97b8df',
      400: '#759fd2',
      500: '#5492d0',
      600: '#3e76b3',
      700: '#335f93',
      800: '#284a72',
      900: '#143960',
      950: '#0c2540',
    },
    formField: {
      paddingX: '0.75rem',
      paddingY: '0.5rem',
      sm: {
        fontSize: '0.875rem',
        paddingX: '0.625rem',
        paddingY: '0.375rem',
      },
      lg: {
        fontSize: '1.125rem',
        paddingX: '0.875rem',
        paddingY: '0.625rem',
      },
      borderRadius: '{border.radius.md}',
      focusRing: {
        width: '0',
        style: 'none',
        color: 'transparent',
        offset: '0',
        shadow: 'none',
      },
      transitionDuration: '{transition.duration}',
    },
    list: {
      padding: '0.25rem 0.25rem',
      gap: '2px',
      header: {
        padding: '0.5rem 1rem 0.25rem 1rem',
      },
      option: {
        padding: '0.5rem 0.75rem',
        borderRadius: '{border.radius.sm}',
      },
      optionGroup: {
        padding: '0.5rem 0.75rem',
        fontWeight: '600',
      },
    },
    content: {
      borderRadius: '{border.radius.md}',
    },
    mask: {
      transitionDuration: '0.15s',
    },
    navigation: {
      list: {
        padding: '0.25rem 0.25rem',
        gap: '2px',
      },
      item: {
        padding: '0.5rem 0.75rem',
        borderRadius: '{border.radius.sm}',
        gap: '0.5rem',
      },
      submenuLabel: {
        padding: '0.5rem 0.75rem',
        fontWeight: '600',
      },
      submenuIcon: {
        size: '0.875rem',
      },
    },
    overlay: {
      select: {
        borderRadius: '{border.radius.md}',
        shadow: 'var(--shadow-card)',
      },
      popover: {
        borderRadius: '{border.radius.md}',
        padding: '0.75rem',
        shadow: 'var(--shadow-card)',
      },
      modal: {
        borderRadius: '{border.radius.xl}',
        padding: '1.25rem',
        shadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
      },
      navigation: {
        shadow: 'var(--shadow-card)',
      },
    },
    colorScheme: {
      light: {
        surface: {
          0: '#ffffff',
          50: '#f4f7fa',
          100: '#eaf0f7',
          200: '#d4dee8',
          300: '#b8c5d3',
          400: '#7490ad',
          500: '#5c7a92',
          600: '#3a546a',
          700: '#143960',
          800: '#0c2540',
          900: '#061732',
          950: '#030c1a',
        },
        primary: {
          color: 'var(--color-accent)',
          contrastColor: '#ffffff',
          hoverColor: 'color-mix(in srgb, var(--color-accent), black 12%)',
          activeColor: 'color-mix(in srgb, var(--color-accent), black 24%)',
        },
        highlight: {
          background: 'var(--color-button-active)',
          focusBackground: 'var(--color-button-active)',
          color: 'var(--color-accent)',
          focusColor: 'var(--color-accent)',
        },
        mask: {
          background: 'rgba(0,0,0,0.4)',
          color: 'var(--color-foreground-muted-2)',
        },
        formField: {
          background: 'var(--color-canvas)',
          disabledBackground: 'var(--color-elevated)',
          filledBackground: 'var(--color-canvas)',
          filledHoverBackground: 'var(--color-canvas)',
          filledFocusBackground: 'var(--color-canvas)',
          borderColor: 'var(--color-divider)',
          hoverBorderColor: 'var(--color-foreground-muted-2)',
          focusBorderColor: 'var(--color-accent)',
          invalidBorderColor: 'var(--color-error)',
          color: 'var(--color-foreground)',
          disabledColor: 'var(--color-foreground-muted-1)',
          placeholderColor: 'var(--color-foreground-muted-1)',
          invalidPlaceholderColor: 'var(--color-error)',
          floatLabelColor: 'var(--color-foreground-muted-1)',
          floatLabelFocusColor: 'var(--color-accent)',
          floatLabelActiveColor: 'var(--color-foreground-muted-1)',
          floatLabelInvalidColor: 'var(--color-error)',
          iconColor: 'var(--color-foreground-muted-2)',
          shadow: 'none',
        },
        text: {
          color: 'var(--color-foreground)',
          hoverColor: 'var(--color-foreground)',
          mutedColor: 'var(--color-foreground-muted-1)',
          hoverMutedColor: 'var(--color-foreground-secondary)',
        },
        content: {
          background: 'var(--color-surface)',
          hoverBackground: 'var(--color-elevated)',
          borderColor: 'var(--color-divider)',
          color: 'var(--color-foreground)',
          hoverColor: 'var(--color-foreground)',
        },
        overlay: {
          select: {
            background: 'var(--color-surface)',
            borderColor: 'var(--color-divider)',
            color: 'var(--color-foreground)',
          },
          popover: {
            background: 'var(--color-surface)',
            borderColor: 'var(--color-divider)',
            color: 'var(--color-foreground)',
          },
          modal: {
            background: 'var(--color-surface)',
            borderColor: 'var(--color-divider)',
            color: 'var(--color-foreground)',
          },
        },
        list: {
          option: {
            focusBackground: 'var(--color-elevated)',
            selectedBackground: 'var(--color-button-active)',
            selectedFocusBackground: 'var(--color-button-active)',
            color: 'var(--color-foreground)',
            focusColor: 'var(--color-foreground)',
            selectedColor: 'var(--color-accent)',
            selectedFocusColor: 'var(--color-accent)',
            icon: {
              color: 'var(--color-foreground-muted-2)',
              focusColor: 'var(--color-foreground-muted-1)',
            },
          },
          optionGroup: {
            background: 'transparent',
            color: 'var(--color-foreground-muted-1)',
          },
        },
        navigation: {
          item: {
            focusBackground: 'var(--color-elevated)',
            activeBackground: 'var(--color-button-active)',
            color: 'var(--color-foreground)',
            focusColor: 'var(--color-foreground)',
            activeColor: 'var(--color-accent)',
            icon: {
              color: 'var(--color-foreground-muted-2)',
              focusColor: 'var(--color-foreground-muted-1)',
              activeColor: 'var(--color-accent)',
            },
          },
          submenuLabel: {
            background: 'transparent',
            color: 'var(--color-foreground-muted-1)',
          },
          submenuIcon: {
            color: 'var(--color-foreground-muted-2)',
            focusColor: 'var(--color-foreground-muted-1)',
            activeColor: 'var(--color-accent)',
          },
        },
      },
      dark: {
        surface: {
          0: '#ffffff',
          50: '#f5f7fb',
          100: '#d0dcee',
          200: '#b0c0d4',
          300: '#7a91a9',
          400: '#4e6980',
          500: '#2d4760',
          600: '#1a2e45',
          700: '#0f1e2e',
          800: '#0e1620',
          900: '#080e14',
          950: '#040810',
        },
        primary: {
          color: 'var(--color-accent)',
          contrastColor: '#ffffff',
          hoverColor: 'color-mix(in srgb, var(--color-accent), white 12%)',
          activeColor: 'color-mix(in srgb, var(--color-accent), white 24%)',
        },
        highlight: {
          background: 'var(--color-button-active)',
          focusBackground: 'var(--color-button-active)',
          color: 'var(--color-accent)',
          focusColor: 'var(--color-accent)',
        },
        mask: {
          background: 'rgba(0,0,0,0.6)',
          color: 'var(--color-foreground-muted-2)',
        },
        formField: {
          background: 'var(--color-canvas)',
          disabledBackground: 'var(--color-elevated)',
          filledBackground: 'var(--color-canvas)',
          filledHoverBackground: 'var(--color-canvas)',
          filledFocusBackground: 'var(--color-canvas)',
          borderColor: 'var(--color-divider)',
          hoverBorderColor: 'var(--color-foreground-muted-2)',
          focusBorderColor: 'var(--color-accent)',
          invalidBorderColor: 'var(--color-error)',
          color: 'var(--color-foreground)',
          disabledColor: 'var(--color-foreground-muted-1)',
          placeholderColor: 'var(--color-foreground-muted-1)',
          invalidPlaceholderColor: 'var(--color-error)',
          floatLabelColor: 'var(--color-foreground-muted-1)',
          floatLabelFocusColor: 'var(--color-accent)',
          floatLabelActiveColor: 'var(--color-foreground-muted-1)',
          floatLabelInvalidColor: 'var(--color-error)',
          iconColor: 'var(--color-foreground-muted-2)',
          shadow: 'none',
        },
        text: {
          color: 'var(--color-foreground)',
          hoverColor: 'var(--color-foreground)',
          mutedColor: 'var(--color-foreground-muted-1)',
          hoverMutedColor: 'var(--color-foreground-secondary)',
        },
        content: {
          background: 'var(--color-surface)',
          hoverBackground: 'var(--color-elevated)',
          borderColor: 'var(--color-divider)',
          color: 'var(--color-foreground)',
          hoverColor: 'var(--color-foreground)',
        },
        overlay: {
          select: {
            background: 'var(--color-surface)',
            borderColor: 'var(--color-divider)',
            color: 'var(--color-foreground)',
          },
          popover: {
            background: 'var(--color-surface)',
            borderColor: 'var(--color-divider)',
            color: 'var(--color-foreground)',
          },
          modal: {
            background: 'var(--color-surface)',
            borderColor: 'var(--color-divider)',
            color: 'var(--color-foreground)',
          },
        },
        list: {
          option: {
            focusBackground: 'var(--color-elevated)',
            selectedBackground: 'var(--color-button-active)',
            selectedFocusBackground: 'var(--color-button-active)',
            color: 'var(--color-foreground)',
            focusColor: 'var(--color-foreground)',
            selectedColor: 'var(--color-accent)',
            selectedFocusColor: 'var(--color-accent)',
            icon: {
              color: 'var(--color-foreground-muted-2)',
              focusColor: 'var(--color-foreground-muted-1)',
            },
          },
          optionGroup: {
            background: 'transparent',
            color: 'var(--color-foreground-muted-1)',
          },
        },
        navigation: {
          item: {
            focusBackground: 'var(--color-elevated)',
            activeBackground: 'var(--color-button-active)',
            color: 'var(--color-foreground)',
            focusColor: 'var(--color-foreground)',
            activeColor: 'var(--color-accent)',
            icon: {
              color: 'var(--color-foreground-muted-2)',
              focusColor: 'var(--color-foreground-muted-1)',
              activeColor: 'var(--color-accent)',
            },
          },
          submenuLabel: {
            background: 'transparent',
            color: 'var(--color-foreground-muted-1)',
          },
          submenuIcon: {
            color: 'var(--color-foreground-muted-2)',
            focusColor: 'var(--color-foreground-muted-1)',
            activeColor: 'var(--color-accent)',
          },
        },
      },
    },
  },
});
